import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Database, Cpu, FlaskConical, Wrench, Building2, FileText, ArrowRight, Share2, Bot, LayoutDashboard, RotateCcw, Factory } from "lucide-react";
import { Reveal, SectionTag } from "@/components/primitives";
import { useTheme } from "@/hooks/use-theme";
import { useLanguage } from "@/hooks/use-language";

const SOURCES = [
  { icon: Database, label: "Process Historian" },
  { icon: Cpu, label: "DCS / PLC / SCADA" },
  { icon: Factory, label: "MES" },
  { icon: Wrench, label: "Maintenance (CMMS)" },
  { icon: FlaskConical, label: "Laboratory" },
  { icon: FileText, label: "Documents & SOPs" },
  { icon: Building2, label: "ERP" },
];

const OUTPUTS = [
  { label: "Dashboards" },
  { label: "Root-Cause" },
  { label: "Alarm Insights" },
  { label: "Recommendations" },
];

const NODE_YS = [80, 160, 240, 320, 400, 480, 560];
const OUT_YS = [110, 250, 390, 530];

const srcPath = (y) => `M 195 ${y} C 300 ${y}, 330 320, 420 ${320 + (y - 320) * 0.12}`;
const metaPath = (y) => `M 460 ${320 + (y - 320) * 0.12} C 530 ${320 + (y - 320) * 0.12}, 560 320, 625 320`;
const fanPath = (y) => `M 1005 320 C 1020 320, 1015 ${y + 24}, 1035 ${y + 24}`;

const DESCS = {
  hub: "Dual-stream context: live machine telemetry fused with your plant's operating knowledge — computed entirely on-premise.",
  boundary: "Raw payloads are abstracted into operational metadata at the perimeter. Models run locally — nothing is exfiltrated to the cloud.",
  agents: "Specialised agents interpret operational intent, retrieve only the relevant context, and assemble answers with a full source trace.",
};

const TOUR = [
  { id: "sources", label: "Step 1 — Your data flows in", text: "Six streams of plant intelligence — historians, control systems, maintenance, lab, documents, enterprise — flow in as raw telemetry." },
  { id: "boundary", label: "Step 2 — The data-blind boundary", text: "At the perimeter, raw payloads are abstracted into operational metadata. Nothing raw ever crosses." },
  { id: "hub", label: "Step 3 — The Knowledge Graph", text: "Machine telemetry is fused with human operating knowledge — SOPs, shift logs, plant rules — computed entirely on-premise." },
  { id: "agents", label: "Step 4 — Specialised agents", text: "Agents interpret your task in operational language and retrieve only the context they need." },
  { id: "outputs", label: "Step 5 — Trusted outputs", text: "Dashboards, root causes, alarm insights and recommendations flow back — every one traceable to source." },
  { id: "cloud", label: "Step 6 — The cloud stays out", text: "The path to frontier cloud AI is blocked by architecture, not by policy. Your telemetry never leaves the plant." },
];

const PALETTES = {
  dark: { teal: "#2dd4bf", green: "#4ade80", boundary: "#14b8a6" },
  light: { teal: "#0d9488", green: "#16a34a", boundary: "#0f766e" },
};

const Pulse = ({ d, dur, begin, fill, r = 4 }) => (
  <circle r={r} fill={fill}>
    <animateMotion dur={dur} begin={begin} repeatCount="indefinite" path={d} />
  </circle>
);

const DataFlow = ({ active, onHover, palette, tr }) => (
  <svg viewBox="0 0 1200 640" className="w-full h-auto" data-testid="dataflow-diagram" role="img" aria-label="Data-blind architecture flow diagram">
    {/* base paths */}
    {NODE_YS.map((y, i) => {
      const hl = active === i || active === "sources";
      return (
        <g key={`p-${i}`}>
          <path d={srcPath(y)} fill="none" stroke={palette.teal} strokeOpacity={hl ? 0.9 : 0.15} strokeWidth={hl ? 2 : 1.5} />
          <path d={metaPath(y)} fill="none" stroke={palette.green} strokeOpacity={hl ? 0.9 : 0.15} strokeWidth={hl ? 2 : 1.5} />
          <Pulse d={srcPath(y)} dur="2.4s" begin={`${i * 0.4}s`} fill={palette.teal} />
          <Pulse d={metaPath(y)} dur="1.6s" begin={`${i * 0.4 + 1}s`} fill={palette.green} />
        </g>
      );
    })}
    {OUT_YS.map((y, i) => {
      const hl = active === `out-${i}` || active === "outputs";
      return (
        <g key={`f-${i}`}>
          <path d={fanPath(y)} fill="none" stroke={palette.green} strokeOpacity={hl ? 0.9 : 0.15} strokeWidth={hl ? 2 : 1.5} />
          <Pulse d={fanPath(y)} dur="1.8s" begin={`${i * 0.5}s`} fill={palette.green} r={3.5} />
        </g>
      );
    })}

    {/* blocked cloud path */}
    <path d="M 420 52 C 520 28, 590 38, 662 56" fill="none" stroke={active === "cloud" ? "#ef4444" : "currentColor"} strokeOpacity={active === "cloud" ? 0.8 : 0.3} className="text-muted-foreground" strokeDasharray="4 7" strokeWidth={active === "cloud" ? 2 : 1.5} />
    <path d="M 536 33 l 18 16 M 554 33 l -18 16" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" />
    <text x="545" y="22" textAnchor="middle" fontSize="10" letterSpacing="2" fill="#ef4444" fontFamily="Chivo, sans-serif" fontWeight="700">{tr.blocked}</text>
    <g onMouseEnter={() => onHover("cloud")} onMouseLeave={() => onHover(null)} className="cursor-pointer">
      <circle cx="700" cy="60" r="34" className="fill-muted stroke-border" strokeDasharray="3 4" strokeWidth={active === "cloud" ? 2 : 1} />
      <text x="700" y="57" textAnchor="middle" fontSize="9" letterSpacing="1.5" className="fill-muted-foreground" fontFamily="Chivo, sans-serif">FRONTIER</text>
      <text x="700" y="69" textAnchor="middle" fontSize="9" letterSpacing="1.5" className="fill-muted-foreground" fontFamily="Chivo, sans-serif">CLOUD</text>
    </g>

    {/* data-blind boundary */}
    <g onMouseEnter={() => onHover("boundary")} onMouseLeave={() => onHover(null)} className="cursor-pointer">
      <rect x="420" y="40" width="40" height="560" rx="2" fill={palette.boundary} fillOpacity={active === "boundary" ? 0.22 : 0.07} stroke={palette.boundary} strokeOpacity={active === "boundary" ? 1 : 0.6} strokeWidth={active === "boundary" ? 2 : 1} strokeDasharray="6 6" />
      <text transform="rotate(-90 440 320)" x="440" y="320" textAnchor="middle" fontSize="11" letterSpacing="4" fill={palette.boundary} fontFamily="Chivo, sans-serif" fontWeight="700">DATA-BLIND BOUNDARY</text>
      <rect x="431" y="26" width="18" height="14" rx="2" fill={palette.boundary} />
      <path d="M 435 26 v-5 a 5 5 0 0 1 10 0 v 5" fill="none" stroke={palette.boundary} strokeWidth="2.5" />
    </g>

    {/* source nodes */}
    {SOURCES.map((s, i) => {
      const hl = active === i || active === "sources";
      return (
        <g key={s.label} onMouseEnter={() => onHover(i)} onMouseLeave={() => onHover(null)} className="cursor-pointer">
          <rect x="25" y={NODE_YS[i] - 28} width="170" height="56" rx="2" className="fill-card" stroke={hl ? palette.boundary : "hsl(var(--border))"} strokeWidth={hl ? 2 : 1} />
          <text x="110" y={NODE_YS[i] + 5} textAnchor="middle" fontSize="13" fontWeight="600" className="fill-foreground" fontFamily="Chivo, sans-serif">{s.label}</text>
        </g>
      );
    })}

    {/* knowledge graph hub */}
    <g onMouseEnter={() => onHover("hub")} onMouseLeave={() => onHover(null)} className="cursor-pointer">
      <circle cx="700" cy="320" r="75" fill={palette.boundary} fillOpacity={active === "hub" ? 0.22 : 0.08} stroke={active === "hub" ? palette.teal : palette.boundary} strokeWidth={active === "hub" ? 2 : 1.5} />
      <circle cx="700" cy="320" r="75" fill="none" stroke={palette.teal}>
        <animate attributeName="r" values="75;98" dur="2.6s" repeatCount="indefinite" />
        <animate attributeName="stroke-opacity" values="0.7;0" dur="2.6s" repeatCount="indefinite" />
      </circle>
      <text x="700" y="312" textAnchor="middle" fontSize="14" fontWeight="700" className="fill-foreground" fontFamily="Chivo, sans-serif">KNOWLEDGE</text>
      <text x="700" y="330" textAnchor="middle" fontSize="14" fontWeight="700" className="fill-foreground" fontFamily="Chivo, sans-serif">GRAPH</text>
      <text x="700" y="348" textAnchor="middle" fontSize="9" letterSpacing="1.5" fill={palette.boundary} fontFamily="Chivo, sans-serif">{tr.runsLocally}</text>
    </g>

    {/* agents */}
    <g onMouseEnter={() => onHover("agents")} onMouseLeave={() => onHover(null)} className="cursor-pointer">
      <rect x="775" y="282" width="210" height="76" rx="2" className="fill-card" stroke={active === "agents" ? palette.boundary : "hsl(var(--border))"} strokeWidth={active === "agents" ? 2 : 1} />
      <text x="880" y="308" textAnchor="middle" fontSize="12" fontWeight="700" className="fill-foreground" fontFamily="Chivo, sans-serif">PROPRIETARY</text>
      <text x="880" y="323" textAnchor="middle" fontSize="12" fontWeight="700" className="fill-foreground" fontFamily="Chivo, sans-serif">ORCHESTRATION</text>
      <text x="880" y="338" textAnchor="middle" fontSize="12" fontWeight="700" className="fill-foreground" fontFamily="Chivo, sans-serif">ENGINE</text>
    </g>
    <path d="M 768 320 L 775 320" stroke={palette.green} strokeOpacity={active === "agents" ? 0.9 : 0.4} strokeWidth="1.5" />
    <path d="M 985 320 L 1005 320" stroke={palette.green} strokeOpacity={active === "outputs" ? 0.9 : 0.4} strokeWidth="1.5" />
    <Pulse d="M 985 320 L 1005 320" dur="0.8s" begin="0s" fill={palette.green} r={3} />

    {/* output nodes */}
    {OUTPUTS.map((o, i) => {
      const hl = active === `out-${i}` || active === "outputs";
      return (
        <g key={o.label} onMouseEnter={() => onHover(`out-${i}`)} onMouseLeave={() => onHover(null)} className="cursor-pointer">
          <rect x="1035" y={OUT_YS[i]} width="150" height="48" rx="2" className="fill-card" stroke={hl ? palette.green : "hsl(var(--border))"} strokeWidth={hl ? 2 : 1} />
          <text x="1110" y={OUT_YS[i] + 29} textAnchor="middle" fontSize="12" fontWeight="600" className="fill-foreground" fontFamily="Chivo, sans-serif">{o.label}</text>
        </g>
      );
    })}
  </svg>
);

export const Architecture = () => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const palette = PALETTES[theme];
  const TOUR = t.arch.tour.map((s, i) => ({ ...s, id: ["sources", "boundary", "hub", "agents", "outputs", "cloud"][i] }));
  const wrapRef = useRef(null);
  const inView = useInView(wrapRef, { once: true, margin: "-200px" });
  const [step, setStep] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (inView && step === -1) {
      setStep(0);
      setPlaying(true);
    }
  }, [inView, step]);

  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => {
      setStep((s) => {
        if (s >= TOUR.length - 1) {
          setPlaying(false);
          return s;
        }
        return s + 1;
      });
    }, 3200);
    return () => clearInterval(t);
  }, [playing]);

  const onHover = (v) => {
    setHovered(v);
    if (v !== null && playing) setPlaying(false);
  };

  const replay = () => {
    setHovered(null);
    setStep(0);
    setPlaying(true);
  };

  const active = hovered !== null ? hovered : step >= 0 ? TOUR[step].id : null;

  const desc =
    hovered !== null
      ? typeof hovered === "number"
        ? t.arch.sourceDescs[hovered]
        : typeof hovered === "string" && hovered.startsWith("out-")
          ? t.arch.outputDescs[Number(hovered.split("-")[1])]
          : t.arch.descs[hovered]
      : step >= 0
        ? TOUR[step].text
        : t.arch.defaultDesc;

  return (
    <section id="architecture" className="py-24 lg:py-32 border-b border-border" data-testid="architecture-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <SectionTag index="03" label={t.arch.tag} />
          <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl max-w-3xl">
            {t.arch.h2}
          </h2>
          <p className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground">
            {t.arch.sub}
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-16 hidden lg:block border border-border p-6" data-testid="architecture-flow" ref={wrapRef}>
            <DataFlow active={active} onHover={onHover} palette={palette} tr={t.arch} />
            <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: palette.teal }} /> {t.arch.legend[0]}</span>
              <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: palette.green }} /> {t.arch.legend[1]}</span>
              <span className="flex items-center gap-2"><span className="font-bold text-red-500">✕</span> {t.arch.legend[2]}</span>
            </div>
            <div className="mt-4 border-t border-border pt-4 flex items-start justify-between gap-6">
              <div className="min-h-[56px] flex-1" data-testid="dataflow-description">
                {hovered === null && step >= 0 && (
                  <p className="font-display text-xs tracking-[0.25em] uppercase text-teal-700 dark:text-teal-400 mb-2" data-testid="tour-step-label">
                    {TOUR[step].label}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">{desc}</p>
                <div className="mt-3 flex items-center gap-1.5" data-testid="tour-progress">
                  {TOUR.map((s, i) => (
                    <span
                      key={s.id}
                      className="h-1 rounded-full transition-[width,background-color] duration-500"
                      style={{
                        width: i === step ? 24 : 8,
                        background: i === step ? palette.teal : "hsl(var(--border))",
                      }}
                    />
                  ))}
                </div>
              </div>
              <button
                onClick={replay}
                data-testid="tour-replay-button"
                className="shrink-0 inline-flex items-center gap-2 h-9 px-4 rounded-full border border-border text-xs font-medium hover:border-teal-500/60 hover:text-teal-700 dark:hover:text-teal-400 transition-colors duration-300"
              >
                <RotateCcw size={13} />
                {t.arch.replay}
              </button>
            </div>
          </div>
        </Reveal>

        <div className="mt-16 lg:hidden space-y-6" data-testid="architecture-flow-mobile">
          <div className="border border-border p-8">
            <p className="font-display text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6">{t.arch.yourData}</p>
            <div className="grid grid-cols-2 gap-3">
              {SOURCES.map((s) => (
                <div key={s.label} className="flex items-center gap-2.5 border border-border px-3 py-2.5 text-xs font-medium">
                  <s.icon size={14} strokeWidth={1.5} className="shrink-0" />
                  {s.label}
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center"><ArrowRight size={20} className="rotate-90 text-teal-500/70" /></div>
          <div className="border border-teal-500/40 bg-teal-500/5 dark:bg-teal-400/5 p-8">
            <Share2 size={26} strokeWidth={1.5} className="text-teal-600 dark:text-teal-400" />
            <h3 className="mt-6 font-display font-bold text-xl">{t.arch.kgMobileTitle}</h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {t.arch.kgMobileBody}
            </p>
          </div>
          <div className="flex justify-center"><ArrowRight size={20} className="rotate-90 text-teal-500/70" /></div>
          <div className="border border-border p-8 space-y-6">
            <div className="flex items-center gap-3">
              <Bot size={22} strokeWidth={1.5} className="text-teal-600 dark:text-teal-400" />
              <h3 className="font-display font-bold text-xl">{t.arch.agentsTitle}</h3>
            </div>
            <div className="flex items-center gap-3">
              <LayoutDashboard size={22} strokeWidth={1.5} className="text-teal-600 dark:text-teal-400" />
              <h3 className="font-display font-bold text-xl">{t.arch.outputsTitle}</h3>
            </div>
          </div>
        </div>

        <Reveal delay={0.25}>
          <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="connector-chips">
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground mr-2">{t.arch.connectors}</span>
            {["OPC-UA", "MQTT", "Native Historian APIs"].map((c) => (
              <span key={c} className="border border-border px-4 py-2 text-xs font-medium tracking-wide hover:border-teal-500/60 transition-colors duration-300">
                {c}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
